/**
 * How long the map should take to go somewhere.
 *
 * A short hop reads better slow: a quarter of a second of travel tells the eye that this is the
 * same place seen from a step away, and the reader keeps their bearings. A long one reads worse
 * slow — a half-second of streets tearing past is not information, it is a wait with motion
 * sickness on top — so the further the camera has to go, the quicker it gets there.
 *
 * Measured in screen pixels at the current zoom rather than in metres, because that is what the
 * eye is actually being asked to follow.
 */

/** Closer than this, and the move is a nudge: the gentle ease. */
export const NEAR_PX = 150;
/** Further than this, and it is a jump: as quick as it goes without being a cut. */
export const FAR_PX = 1200;
const SLOW_MS = 280;
const FAST_MS = 90;

export function tweenMs(distancePx: number): number {
  if (!Number.isFinite(distancePx) || distancePx <= NEAR_PX) return SLOW_MS;
  if (distancePx >= FAR_PX) return FAST_MS;
  const t = (distancePx - NEAR_PX) / (FAR_PX - NEAR_PX);
  return Math.round(SLOW_MS + (FAST_MS - SLOW_MS) * t);
}

/**
 * Whether a move is far enough that travelling it is worse than being there.
 *
 * A long slide crosses ground the map holds no tiles for, so every frame of it is painted with
 * whatever is to hand — blurred, blank, or the wrong zoom — and everything pinned to that ground
 * is redrawn against it. The eye reads that as flicker, not as motion. Beyond a screen or so the
 * honest thing is to arrive.
 */
export function isJump(distancePx: number): boolean {
  return Number.isFinite(distancePx) && distancePx >= FAR_PX;
}

/**
 * How far from the middle the needle may wander before the map goes after it, as a share of the
 * map's shorter side. Small, because the point is to keep it near the middle — but not zero,
 * because a map that corrects every pixel of drift shudders under the thumb, and the pins
 * pinned to it shudder with it. Getting on for half the shorter side — about 160 px across on a
 * phone — which swallows a good stretch of walking before the view is dragged along at all.
 */
export const DEAD_ZONE = 0.44;

/** The dead zone's radius in screen pixels, for a map of this size. */
export function deadZoneRadius(viewport: { width: number; height: number }): number {
  return (Math.min(viewport.width, viewport.height) * DEAD_ZONE) / 2;
}

/**
 * How far the camera should move, given where the needle has landed on screen.
 *
 * The game-maker's answer rather than the cartographer's: inside the circle nothing happens at
 * all, and outside it the camera moves by exactly the amount the needle went over — so the
 * needle comes to rest on the edge of the circle it pushed against, not snapped back to the
 * middle. The view follows the way a camera follows a character: it is dragged along, it does
 * not chase.
 *
 * Null means stay exactly where you are.
 */
/**
 * Corrections smaller than this are not made at all.
 *
 * The other half of the game camera's trick, after the dead zone: a camera that answers every
 * pixel of overshoot and a subject that keeps moving trade one-pixel corrections back and forth
 * for as long as the subject moves, and that trade is what the eye reads as a shimmer. A few
 * pixels of slack costs nothing — nobody can see the needle sitting four pixels off the rim it
 * pushed against — and it ends the conversation.
 */
export const SLACK_PX = 4;

export function followBy(
  offsetPx: { x: number; y: number },
  viewport: { width: number; height: number },
): { x: number; y: number } | null {
  const limit = deadZoneRadius(viewport);
  const away = Math.hypot(offsetPx.x, offsetPx.y);
  // A map with no size yet cannot hold anything inside a zone: go to it.
  if (!(limit > 0)) return offsetPx;
  if (away <= limit || away === 0) return null;
  const over = (away - limit) / away;
  const by = { x: offsetPx.x * over, y: offsetPx.y * over };
  // Not worth a camera move: leave it where it is and let the needle drift the last few pixels.
  if (Math.hypot(by.x, by.y) < SLACK_PX) return null;
  return by;
}

/**
 * How long the dial must be left alone before the map tidies up after it.
 *
 * A quarter of a second: long enough that it cannot fire between two turns of the same drag —
 * the finger has to be off the dial as well — and short enough that letting go and looking up
 * finds the map already settled on where you were, rather than waiting for it.
 */
export const SETTLE_MS = 250;
